import React, { useEffect, useState } from "react";
import { Plus, X, Paperclip, Trash2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useVault } from "@/context/VaultContext";
import { encryptJson, encryptBytes } from "@/lib/vaultCrypto";
import { VAULT_CATEGORIES, emptyEntry, formatBytes } from "@/lib/vaultMeta";
import { toast } from "sonner";
import { markLogged } from "@/lib/vaultReminders";

const MAX_FILE = 10 * 1024 * 1024;

function PeopleList({ label, people, onChange, testid, addLabel }) {
  const set = (i, k, v) => onChange(people.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  return (
    <div>
      <Label>{label}</Label>
      <div className="space-y-2 mt-1.5">
        {people.map((p, i) => (
          <div key={i} className="flex gap-2">
            <Input data-testid={`${testid}-name-${i}`} placeholder="Name" value={p.name} onChange={(e) => set(i, "name", e.target.value)} />
            <Input data-testid={`${testid}-title-${i}`} placeholder="Title / role" value={p.title} onChange={(e) => set(i, "title", e.target.value)} />
            <Button type="button" variant="ghost" size="icon" onClick={() => onChange(people.filter((_, idx) => idx !== i))} className="shrink-0 text-slate-400 hover:text-rose-600" aria-label="Remove"><X size={16} /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" data-testid={`${testid}-add`} onClick={() => onChange([...people, { name: "", title: "" }])} className="rounded-full gap-1"><Plus size={14} /> {addLabel}</Button>
      </div>
    </div>
  );
}

function SupervisorPicker({ value, name, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => api.get(`/supervisors?search=${encodeURIComponent(q)}&limit=6`).then((r) => setResults(r.data.items)), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) {
    return (
      <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm" data-testid="vault-supervisor-selected">
        <span>Linked to <b>{name}</b> <span className="text-slate-400">(private — only you see this link)</span></span>
        <button type="button" onClick={() => onPick("", "")} className="text-slate-400 hover:text-rose-600" data-testid="vault-supervisor-clear"><X size={15} /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <Input data-testid="vault-supervisor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a supervisor on my360boss (optional)" className="pl-9" />
      {results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          {results.map((s) => (
            <button type="button" key={s.id} data-testid={`vault-supervisor-option-${s.id}`} onClick={() => { onPick(s.id, `${s.name} · ${s.company}`); setQ(""); setResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">
              <b>{s.name}</b> <span className="text-slate-500">· {s.title}, {s.company}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VaultEntryForm({ open, onOpenChange, existing }) {
  const { vaultKey, loadEntries } = useVault();
  const [form, setForm] = useState(emptyEntry());
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) { setForm(existing ? { ...emptyEntry(), ...existing.data } : emptyEntry()); setFiles([]); setProgress(""); }
  }, [open, existing]);

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const tooBig = picked.find((f) => f.size > MAX_FILE);
    if (tooBig) toast.error(`${tooBig.name} is over 10 MB`);
    setFiles((fs) => [...fs, ...picked.filter((f) => f.size <= MAX_FILE)].slice(0, 8));
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.what_happened.trim()) return toast.error("Describe what happened");
    setBusy(true);
    try {
      const clean = {
        ...form,
        individuals: form.individuals.filter((p) => p.name.trim() || p.title.trim()),
        witnesses: form.witnesses.filter((p) => p.name.trim() || p.title.trim()),
      };
      setProgress("Encrypting entry...");
      const enc = await encryptJson(vaultKey, clean);
      const res = existing ? await api.put(`/vault/entries/${existing.id}`, enc) : await api.post("/vault/entries", enc);
      const entryId = res.data.id;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress(`Encrypting & uploading attachment ${i + 1} of ${files.length}...`);
        const bytes = await encryptBytes(vaultKey, await f.arrayBuffer());
        const meta = await encryptJson(vaultKey, { name: f.name, type: f.type, size: f.size });
        const fd = new FormData();
        fd.append("file", new Blob([bytes], { type: "application/octet-stream" }), "encrypted.bin");
        fd.append("meta_ciphertext", meta.ciphertext);
        fd.append("meta_iv", meta.iv);
        await api.post(`/vault/entries/${entryId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await loadEntries();
      if (!existing) markLogged();
      toast.success(existing ? "Entry updated" : "Entry saved to your vault");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save entry");
    } finally {
      setBusy(false); setProgress("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="vault-entry-form">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" style={{ fontWeight: 800 }}>{existing ? "Amend entry" : "New vault entry"}</DialogTitle>
          <DialogDescription>Write it while it's fresh. Be factual and specific: who, what, when, where, who saw it.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <Label htmlFor="ve-title">Short title</Label>
              <Input id="ve-title" data-testid="vault-entry-title" value={form.title} onChange={set("title")} placeholder="e.g. Comment about my appearance in team meeting" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="ve-date">Date of incident</Label>
              <Input id="ve-date" data-testid="vault-entry-date" type="date" required value={form.incident_date} onChange={set("incident_date")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="ve-time">Time <span className="text-slate-400">(approx.)</span></Label>
              <Input id="ve-time" data-testid="vault-entry-time" type="time" value={form.incident_time} onChange={set("incident_time")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="ve-loc">Location</Label>
              <Input id="ve-loc" data-testid="vault-entry-location" value={form.location} onChange={set("location")} placeholder="Conference room B, Slack DM..." className="mt-1" />
            </div>
          </div>

          <div>
            <Label>Category</Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="vault-entry-categories">
              {VAULT_CATEGORIES.map((c) => (
                <button type="button" key={c.key} data-testid={`vault-category-${c.key}`} onClick={() => setForm((f) => ({ ...f, category: c.key }))}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.category === c.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="ve-what">What happened</Label>
            <Textarea id="ve-what" data-testid="vault-entry-what" required rows={6} value={form.what_happened} onChange={set("what_happened")} className="mt-1" placeholder="Exactly what was said or done, in order. Quote words if you can. Note how you responded and how it affected you." />
          </div>

          <PeopleList label="Who was involved" people={form.individuals} onChange={(v) => setForm((f) => ({ ...f, individuals: v }))} testid="vault-individual" addLabel="Add person" />
          <PeopleList label="Witnesses (if any)" people={form.witnesses} onChange={(v) => setForm((f) => ({ ...f, witnesses: v }))} testid="vault-witness" addLabel="Add witness" />

          <div>
            <Label>Related supervisor</Label>
            <div className="mt-1.5"><SupervisorPicker value={form.supervisor_id} name={form.supervisor_name} onPick={(id, name) => setForm((f) => ({ ...f, supervisor_id: id, supervisor_name: name }))} /></div>
          </div>

          <div>
            <Label>Attachments <span className="text-slate-400">(screenshots, emails, photos — encrypted before upload, 10 MB each)</span></Label>
            <div className="mt-1.5 space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid={`vault-pending-file-${i}`}>
                  <span className="flex items-center gap-2 truncate"><Paperclip size={14} className="text-slate-400" /> {f.name} <span className="text-slate-400">{formatBytes(f.size)}</span></span>
                  <button type="button" onClick={() => setFiles((fs) => fs.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                </div>
              ))}
              <label className="inline-flex items-center gap-2 text-sm border border-dashed border-slate-300 rounded-lg px-3 py-2 cursor-pointer hover:border-emerald-500 text-slate-600">
                <Paperclip size={15} /> Add files
                <input data-testid="vault-file-input" type="file" multiple className="hidden" onChange={addFiles} accept="image/*,.pdf,.txt,.eml,.msg,.doc,.docx" />
              </label>
            </div>
          </div>

          {progress && <p className="text-sm text-emerald-700 font-mono" data-testid="vault-save-progress">{progress}</p>}
          <Button data-testid="vault-entry-submit" type="submit" disabled={busy} className="w-full h-11 bg-slate-900 hover:bg-emerald-600 rounded-full">
            {busy ? "Saving..." : existing ? "Save amendment" : "Encrypt & save entry"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
